import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authFetch } from '../../utils/auth';
import { draftSubject } from './soknadChildren';
import RestartDialog from './RestartDialog';

/**
 * SoknadPreview - Preview and download page for generated søknad
 *
 * Shows the draft in the URL (/soknad/:type/:draftId/preview).
 *
 * Features:
 * - Displays generated søknad text by section
 * - Copy buttons for individual sections and full text
 * - Download as .docx button
 * - Edit selections button (returns to wizard)
 * - Start fresh button (a new, empty draft for the same child)
 */
export default function SoknadPreview() {
  const { type, draftId } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);

  // Section order and display names
  const SECTION_ORDER = [
    { key: 'morning', title: 'Behov for hjelp på morgenen' },
    { key: 'daytime', title: 'Behov for hjelp på dagen' },
    { key: 'evening', title: 'Behov for hjelp på kvelden' },
    { key: 'night', title: 'Behov for hjelp på natt' },
    { key: 'additional', title: 'Annet du tenker er relevant for søknaden' },
  ];

  // Load draft data
  const loadDraft = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load full draft data
      let draftResponse;
      try {
        draftResponse = await authFetch(`/soknad/${type}/drafts/${draftId}`);
      } catch (err) {
        // Not one of the user's drafts (deleted, someone else's, or not an
        // id): to the overview
        if (err?.status === 404) {
          navigate(`/soknad/${type}`, { replace: true });
          return;
        }
        throw err;
      }
      if (!draftResponse.ok) {
        throw new Error('Kunne ikke laste søknadsdata');
      }

      const draftData = await draftResponse.json();

      // Check if draft has generated content
      if (!draftData.generatedContent?.fullText) {
        // No generated content, redirect back to wizard
        navigate(`/soknad/${type}/${draftId}`);
        return;
      }

      setDraft(draftData);
    } catch (err) {
      console.error('[SoknadPreview] Load error:', err);
      setError(err.message || 'En feil oppstod ved lasting av søknaden');
    } finally {
      setLoading(false);
    }
  }, [type, draftId, navigate]);

  useEffect(() => {
    const el = document.getElementById('main-scroll-container');
    if (el) el.scrollTop = 0;
  }, []);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  // Copy section text to clipboard
  const handleCopySection = async (sectionKey, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(sectionKey);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Copy full text to clipboard
  const handleCopyAll = async () => {
    if (!draft?.generatedContent?.fullText) return;

    try {
      await navigator.clipboard.writeText(draft.generatedContent.fullText);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download as .docx
  const handleDownload = async () => {
    if (!draft?._id) {
      console.error('[SoknadPreview] No draft ID for download');
      alert('Ingen søknad funnet. Last siden på nytt og prøv igjen.');
      return;
    }

    setDownloading(true);

    try {
      // Build the download URL
      const downloadUrl = `/soknad/${type}/drafts/${draft._id}/download`;

      const response = await authFetch(downloadUrl);

      // Check if the response is ok and has the right content type
      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = 'Ukjent feil';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Verify we got a document (not JSON error)
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Serveren returnerte en feil');
      }

      // Get the blob from response
      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('Dokumentet er tomt. Prøv å generere søknaden på nytt.');
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'hjelpestonad-soknad.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('[SoknadPreview] Download error:', err);
      // Show more detailed error to help debugging
      const errorMessage = err.message || 'Ukjent feil';
      alert(`Kunne ikke laste ned dokumentet: ${errorMessage}`);
    } finally {
      setDownloading(false);
    }
  };

  // Go back to wizard to edit selections
  const handleEditSelections = () => {
    navigate(`/soknad/${type}/${draft._id}`);
  };

  // Parse section content to extract Hvem and Antall timer
  const parseSectionMeta = (sectionText) => {
    if (!sectionText) return { text: '', hvem: null, timer: null };

    const lines = sectionText.split('\n');
    let hvem = null;
    let timer = null;
    const textLines = [];

    lines.forEach(line => {
      const hvemMatch = line.match(/^Hvem:\s*(.+)$/i);
      const timerMatch = line.match(/^Antall timer:\s*(.+)$/i);

      if (hvemMatch) {
        hvem = hvemMatch[1].trim();
      } else if (timerMatch) {
        timer = timerMatch[1].trim();
      } else {
        textLines.push(line);
      }
    });

    return {
      text: textLines.join('\n').trim(),
      hvem,
      timer,
    };
  };

  // Render formatted text (convert markdown-like to readable)
  const renderFormattedText = (text) => {
    if (!text) return null;

    // Split into paragraphs
    const paragraphs = text.split('\n\n').filter(p => p.trim());

    return paragraphs.map((paragraph, idx) => (
      <p key={idx} className="mb-4 last:mb-0 text-text-primary leading-relaxed">
        {paragraph}
      </p>
    ));
  };

  // Get child's name from profile snapshot
  const childName = draft?.profileSnapshot?.child?.alias || 'barnet';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-text-secondary">Laster søknad...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 bg-error-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Noe gikk galt</h2>
          <p className="text-text-secondary mb-6">{error}</p>
          <button
            onClick={loadDraft}
            className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors"
          >
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-text-primary">
            Din hjelpestønad-søknad
          </h1>
          <p className="text-text-secondary mt-1">
            for {childName}
          </p>
        </div>

        {/* Success banner + next steps */}
        <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 sm:p-6 mb-8">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-sage-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium text-sage-800">Søknaden er klar!</p>
              <p className="text-sm text-sage-700 mt-1">
                Du kan kopiere teksten eller laste den ned som Word-dokument for å bruke i Navs skjema.
              </p>
            </div>
          </div>

          {/* Next steps */}
          <div className="mt-4 pt-4 border-t border-sage-200">
            <h3 className="font-semibold text-sage-800 mb-3">Neste steg</h3>
            <ol className="space-y-2 text-sm text-sage-700">
              <li className="flex gap-2">
                <span className="font-semibold">1.</span>
                <span>Kopier teksten eller last ned Word-dokumentet</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold">2.</span>
                <span>Logg inn på nav.no med BankID</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold">3.</span>
                <span>Fyll ut søknadsskjemaet med teksten herfra</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold">4.</span>
                <span>Last opp relevante vedlegg (legeerklæringer, epikriser)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold">5.</span>
                <span>Send inn søknaden</span>
              </li>
            </ol>
          </div>
        </div>

        {/* Action buttons - top */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <button
            onClick={handleCopyAll}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary-50 transition-colors"
          >
            {copiedAll ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Kopiert!
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Kopier hele søknaden
              </>
            )}
          </button>

          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                Laster ned...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Last ned som Word-dokument
              </>
            )}
          </button>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {SECTION_ORDER.map(({ key, title }) => {
            const sectionContent = draft?.generatedContent?.sections?.[key];
            if (!sectionContent) return null;

            const { text, hvem, timer } = parseSectionMeta(sectionContent);

            return (
              <div key={key} className="bg-white rounded-xl border border-border-light overflow-hidden">
                {/* Section header */}
                <div className="flex items-center justify-between p-4 bg-background-card border-b border-border-light">
                  <h3 className="font-semibold text-text-primary">{title}</h3>
                  <button
                    onClick={() => handleCopySection(key, sectionContent)}
                    className="flex items-center gap-1.5 text-sm text-primary hover:text-primary-600 font-medium transition-colors"
                  >
                    {copiedSection === key ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Kopiert!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Kopier tekst
                      </>
                    )}
                  </button>
                </div>

                {/* Section content */}
                <div className="p-4 sm:p-6">
                  <div className="prose prose-sm max-w-none">
                    {renderFormattedText(text)}
                  </div>

                  {/* Meta info */}
                  {(hvem || timer) && (
                    <div className="mt-4 pt-4 border-t border-border-light flex flex-wrap gap-4 text-sm">
                      {hvem && (
                        <div>
                          <span className="text-text-secondary">Hvem: </span>
                          <span className="font-medium text-text-primary">{hvem}</span>
                        </div>
                      )}
                      {timer && (
                        <div>
                          <span className="text-text-secondary">Antall timer: </span>
                          <span className="font-medium text-text-primary">{timer}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* AI disclaimer — small italic note under the generated text */}
        <p className="mt-4 text-xs italic text-text-muted leading-relaxed">
          Dette utkastet er generert med AI basert på informasjonen du har oppgitt. Les gjennom og rett opp eventuelle feil før du sender til Nav.
        </p>

        {/* Bottom actions */}
        <div className="mt-8 pt-8 border-t border-border-light">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleEditSelections}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border text-text-primary font-medium hover:bg-neutral-50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Rediger valg
            </button>

            <button
              onClick={() => setShowRestartConfirm(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-error-200 text-error-600 font-medium hover:bg-error-50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Start på nytt
            </button>
          </div>
        </div>

        {/* Back to overview — moved to the bottom */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-text-secondary hover:text-primary transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Tilbake til oversikt
          </button>
        </div>
      </div>

      {/* Restart confirmation: a new, empty draft for the same child */}
      {showRestartConfirm && (
        <RestartDialog
          type={type}
          draftId={draft._id}
          subject={draftSubject(draft)}
          childName={childName}
          hasGeneratedText
          onCancel={() => setShowRestartConfirm(false)}
          onRestarted={(newDraftId) => navigate(`/soknad/${type}/${newDraftId}`)}
        />
      )}
    </div>
  );
}
